/**
 * FullscreenManager - Utility for managing component visibility during fullscreen mode
 * Provides consistent behavior for hiding/showing competing UI elements
 */
export class FullscreenManager {
  constructor() {
    this.hiddenComponents = new Set();
  }

  /**
   * Hide components that should not be visible during fullscreen
   * @param {string[]} componentIds - Array of element IDs to hide
   */
  hideComponents(componentIds) {
    componentIds.forEach(id => {
      const element = document.getElementById(id);
      if (element && !element.classList.contains('hidden')) {
        element.classList.add('hidden');
        this.hiddenComponents.add(id);
      }
    });
  }

  /**
   * Show previously hidden components
   */
  showComponents() {
    this.hiddenComponents.forEach(id => {
      const element = document.getElementById(id);
      if (element) {
        element.classList.remove('hidden');
      }
    });
    this.hiddenComponents.clear();
  }

  /**
   * Hide resizer element during fullscreen
   * @param {UIElement} resizer - The resizer element to hide
   */
  hideResizer(resizer) {
    if (resizer) {
      resizer.addClass('hidden');
    }
  }

  /**
   * Show resizer element when exiting fullscreen
   * @param {UIElement} resizer - The resizer element to show
   */
  showResizer(resizer) {
    if (resizer) {
      resizer.removeClass('hidden');
    }
  }

  /**
   * Complete cleanup - show all hidden components and resizer
   * @param {UIElement} resizer - Optional resizer to show
   */
  cleanup(resizer = null) {
    this.showComponents();
    if (resizer) {
      this.showResizer(resizer);
    }
  }
}

/**
 * Preset configurations for different component types
 */
export const FULLSCREEN_PRESETS = {
  DRAWER: ['drawer-content-wrapper', 'drawer-global'],
  PREVIEW: ['page-content-wrapper', 'preview-container'],
};

/**
 * Global event coordination for fullscreen states
 * Ensures only one component can be fullscreen at a time
 */
export class FullscreenCoordinator {
  static currentFullscreen = null;

  static setFullscreen(componentName, instance) {
    // If another component is fullscreen, exit it first
    if (this.currentFullscreen && this.currentFullscreen !== componentName) {
      window.dispatchEvent(
        new CustomEvent(`${this.currentFullscreen}:exit-fullscreen`),
      );
    }

    this.currentFullscreen = componentName;
    window.dispatchEvent(
      new CustomEvent('fullscreen:changed', {
        detail: { component: componentName, instance },
      }),
    );
  }

  static exitFullscreen() {
    if (this.currentFullscreen) {
      const previous = this.currentFullscreen;
      this.currentFullscreen = null;
      window.dispatchEvent(
        new CustomEvent('fullscreen:changed', {
          detail: { component: null, previous },
        }),
      );
    }
  }

  static isFullscreen(componentName = null) {
    return componentName
      ? this.currentFullscreen === componentName
      : !!this.currentFullscreen;
  }
}
